// Utility to load and parse SEC (Section) data from CSV
// Maps section IDs to their positions and targets

/**
 * Parse CSV text into structured SEC data
 * @param {string} csvText - Raw CSV text content
 * @returns {Object} - Structured data keyed by SEC ID
 */
export function parseSECData(csvText) {
  const lines = csvText.split('\n').filter(line => line.trim() && !line.startsWith('Table'))
  
  // Remove header row
  const dataLines = lines.slice(1)
  
  // Map sections by SEC ID
  const secMap = {}
  
  dataLines.forEach(line => {
    // Parse CSV line: split on commas not inside quotes
    const matches = line.match(/(".*?"|[^,]+)(?=,|$)/g)
    if (!matches || matches.length < 4) return
    
    const [section, sec, pos, tar] = matches.map(m => m.replace(/^"|"$/g, '').trim())
    
    if (!sec || !sec.startsWith('SEC')) return
    
    // Create SEC entry
    secMap[sec] = {
      id: sec,
      section: section,
      position: pos,
      target: tar
    }
  })
  
  return secMap
}

// Module-level cache — the fetch Promise is reused across all callers so the
// CSV is only downloaded once per page load, regardless of how many components
// call loadSECData() or how often selectedSec changes.
let _secDataPromise = null

/**
 * Load SEC data from CSV file (cached after first load)
 * @returns {Promise<Object>} - Structured SEC data
 */
export async function loadSECData() {
  if (!_secDataPromise) {
    _secDataPromise = (async () => {
      try {
        const response = await fetch('/SEC_Data_table_Gold.csv')
        if (!response.ok) {
          throw new Error(`Failed to load SEC data: ${response.statusText}`)
        }
        const csvText = await response.text()
        return parseSECData(csvText)
      } catch (error) {
        _secDataPromise = null // allow retry on next call
        return {}
      }
    })()
  }
  return _secDataPromise
}

/**
 * Get SEC data for a specific SEC ID
 * @param {Object} secDataMap - The parsed SEC data map
 * @param {string} secId - The SEC ID (e.g., "SEC001", "SEC002")
 * @returns {Object|null} - SEC data or null if not found
 */
export function getSECDataById(secDataMap, secId) {
  if (!secDataMap || !secId) return null
  return secDataMap[secId] || null
}

